import HomeLink from "components/HomeLink";
import { Link, navigate } from "gatsby";
import React, { PropsWithChildren, useEffect, useState } from "react";
import { DownloadURL } from "constants/download";
import { OS, getDeviceOS } from "utils/detect-os";
import Banner from "./Banner";
import { useLocation } from "@reach/router";
import { URLwithUTMSuffix } from "utils/download";
import "flag-icons/css/flag-icons.min.css";
import { useTranslation } from "react-i18next";
import NavMobileToggle from "images/icons/nav-mobile-toggle";
import { SiGithub } from "@icons-pack/react-simple-icons";
import starData from "../data/github-stars.json";

export interface NavbarProps {
    /**
     * The path for this current page
     *
     * @see LayoutProps.path
     */
    path?: string;
    isPageAuth?: boolean;
    shouldShowBanner?: boolean;
}

const CustomNavbar: React.FC<NavbarProps> = ({
    path,
    isPageAuth,
    shouldShowBanner = false,
}) => {
    // Normalize the path for comparison by removing any trailing slash
    const location = useLocation();

    const currentPath = location.pathname.replace(/\/$/, "");

    let shouldBeGray = false;

    const nonGrayPaths = [
        "",
        "/de",
        "/fr",
        "/it",
        "/tr",
        "/es",
        "/nl",
        "/take-control",
    ];

    shouldBeGray = !nonGrayPaths.includes(currentPath ?? "");

    const [expanded, setExpanded] = useState(false);

    interface NavLinkProps {
        /* The path to the current page, without any trailing slashes */
        currentPath?: string;
        to: string;
    }

    const CustomNavLink: React.FC<PropsWithChildren<NavLinkProps>> = ({
        currentPath,
        to,
        children,
    }) => {
        const isCurrentPage = currentPath?.startsWith(to);

        // Check if the link is external by looking for http/https or different domain
        const isExternal =
            /^(https?:)?\/\//.test(to) ||
            (to.startsWith("//") && !to.startsWith("//localhost"));

        const handleLinkClick = (
            event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        ) => {
            // Handle internal navigation
            if (!isExternal) {
                if (
                    (currentPath === "/auth" || currentPath === "/") &&
                    to === "/#pricing"
                ) {
                    event.preventDefault();
                    const redirectSection = document.getElementById(
                        to.substring(2),
                    );
                    if (redirectSection) {
                        redirectSection.scrollIntoView({
                            behavior: "smooth",
                            block: "end",
                        });
                    }
                } else {
                    navigate(to);
                }
                setTimeout(() => {
                    setExpanded(false);
                }, 150);
            }
        };

        // For external links, use a regular anchor tag with target="_blank"
        if (isExternal) {
            return (
                <a
                    href={to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${isCurrentPage ? "navbar-link navbar-link-active" : "navbar-link"}`}
                >
                    {children}
                </a>
            );
        }

        // For internal links, use the existing Link component
        return isCurrentPage ? (
            <Link
                onClick={handleLinkClick}
                to={to}
                aria-current="page"
                className="navbar-link navbar-link-active"
            >
                {children}
            </Link>
        ) : (
            <Link onClick={handleLinkClick} to={to} className="navbar-link">
                {children}
            </Link>
        );
    };

    const authPlayURL = URLwithUTMSuffix(DownloadURL.authGooglePlay);
    const authAppStoreURL = URLwithUTMSuffix(DownloadURL.authAppStore);
    const photosPlayURL = URLwithUTMSuffix(DownloadURL.googlePlay);
    const photosAppStoreURL = URLwithUTMSuffix(DownloadURL.appStore);

    const handleClick = () => {
        const os = getDeviceOS();

        const androidDownloadLink = isPageAuth ? authPlayURL : photosPlayURL;
        const iosDownloadLink = isPageAuth
            ? authAppStoreURL
            : photosAppStoreURL;
        const downloadSectionID = isPageAuth ? "download-auth" : "download";
        if (os === OS.Android) {
            window.location.href = androidDownloadLink;
        } else if (os === OS.IOS) {
            window.location.href = iosDownloadLink;
        } else {
            if (isPageAuth) {
                const downloadSection =
                    document.getElementById(downloadSectionID);
                if (downloadSection) {
                    downloadSection.scrollIntoView({
                        behavior: "smooth",
                        block: "end",
                    });
                }
            } else {
                window.location.href = "https://web.ente.io";
            }
        }
    };

    let pageIDForCTA = "";

    if (isPageAuth) {
        pageIDForCTA = "auth";
    } else {
        pageIDForCTA = "photos";
    }

    const { i18n, t } = useTranslation();

    const [activeLanguage, setActiveLanguage] = useState(i18n.language || "en");

    useEffect(() => {
        setActiveLanguage(i18n.language);
    }, [i18n.language]);

    const localizedPaths = ["/", "/download", "/about"];

    const getLocalizedPath = (basePath: string) => {
        const [pathWithoutHash, hash] = basePath.split("#");

        const cleanPath = pathWithoutHash === "" ? "/" : pathWithoutHash;

        const localizedBase =
            activeLanguage === "en"
                ? cleanPath
                : localizedPaths.includes(cleanPath)
                  ? `/${activeLanguage}${cleanPath === "/" ? "" : cleanPath}`
                  : cleanPath;

        return hash ? `${localizedBase}#${hash}` : localizedBase;
    };

    return (
        <>
            {shouldShowBanner ? <Banner /> : <></>}
            <div
                className={`fixed top-0 z-50 w-full px-4 pt-3 lg:hidden ${expanded ? "flex h-dvh flex-col bg-white" : ""}`}
            >
                <div
                    className={`flex h-12 w-full items-center justify-between rounded-full ${shouldBeGray ? "bg-ente-light-gray" : "bg-white"} px-3`}
                >
                    <HomeLink url={getLocalizedPath("/")} />
                    <div className="flex flex-row items-center justify-between gap-4">
                        <button
                            id={`${pageIDForCTA}-download-button-navbar`}
                            onClick={handleClick}
                            className="text-sm! font-medium text-[#333333]"
                        >
                            {isPageAuth
                                ? "Setup Auth"
                                : t("EP_INDEX_DOWNLOAD_CTA")}
                        </button>
                        <div
                            aria-controls="navbar-main-nav"
                            onClick={() => setExpanded(!expanded)}
                        >
                            <NavMobileToggle className="w-4" />
                        </div>
                    </div>
                </div>
                {expanded && (
                    <div
                        id="navbar-main-nav"
                        className="mt-16 ml-4 flex flex-col gap-3 lg:hidden"
                    >
                        <CustomNavLink
                            {...{ currentPath }}
                            to={getLocalizedPath("/#pricing")}
                        >
                            {t("NAVBAR_PRICING")}
                        </CustomNavLink>
                        <CustomNavLink {...{ currentPath }} to="/blog">
                            {t("NAVBAR_BLOG")}
                        </CustomNavLink>
                        <CustomNavLink
                            {...{ currentPath }}
                            to={getLocalizedPath("/about")}
                        >
                            {t("NAVBAR_ABOUT")}
                        </CustomNavLink>
                        <CustomNavLink
                            {...{ currentPath }}
                            to="https://ente.io/help"
                        >
                            {t("NAVBAR_HELP")}
                        </CustomNavLink>
                        <CustomNavLink
                            {...{ currentPath }}
                            to="https://web.ente.io"
                        >
                            {t("NAVBAR_LOGIN")}
                        </CustomNavLink>
                    </div>
                )}
            </div>
            <div className="hidden lg:fixed lg:top-16 lg:left-1/2 lg:z-50 lg:flex lg:w-full lg:max-w-[1337px] lg:-translate-x-1/2 lg:-translate-y-1/2 lg:transform lg:items-center lg:justify-between">
                <div
                    className={`mx-2 flex h-[4.5rem] w-full items-center justify-between rounded-full ${shouldBeGray ? "bg-ente-light-gray" : "bg-white"} pr-[0.6rem] pl-7`}
                >
                    <HomeLink url={getLocalizedPath("/")} />
                    <div className="ml-[8.9rem] flex flex-row gap-4">
                        <CustomNavLink
                            {...{ currentPath }}
                            to={getLocalizedPath("/#pricing")}
                        >
                            {t("NAVBAR_PRICING")}
                        </CustomNavLink>
                        <CustomNavLink {...{ currentPath }} to="/blog">
                            {t("NAVBAR_BLOG")}
                        </CustomNavLink>
                        <CustomNavLink
                            {...{ currentPath }}
                            to={getLocalizedPath("/about")}
                        >
                            {t("NAVBAR_ABOUT")}
                        </CustomNavLink>
                        <CustomNavLink
                            {...{ currentPath }}
                            to={getLocalizedPath("/download")}
                        >
                            {t("EP_INDEX_DOWNLOAD_CTA")}
                        </CustomNavLink>
                        <CustomNavLink
                            {...{ currentPath }}
                            to="https://ente.io/help"
                        >
                            {t("NAVBAR_HELP")}
                        </CustomNavLink>
                    </div>
                    <div className="flex flex-row items-center justify-center gap-2">
                        <a
                            className="mr-4 flex flex-row items-center justify-center gap-2 no-underline!"
                            href="https://github.com/ente-io/ente"
                            target="_blank"
                        >
                            <SiGithub width={22} />
                            <span className="font-semibold">
                                {Math.trunc(starData.count / 1000)}k
                            </span>
                        </a>
                        <button
                            id={`${pageIDForCTA}-download-button-navbar`}
                            onClick={handleClick}
                            className={`${isPageAuth ? "bg-[#6424bd] hover:bg-[#4b1b8e]!" : "bg-[#2c2c2c] hover:bg-black!"} flex h-[3.5rem] w-[10rem] cursor-pointer items-center justify-center rounded-full! text-lg! font-semibold text-white transition duration-300`}
                        >
                            {isPageAuth
                                ? "Setup Auth"
                                : t("EP_INDEX_SIGN_UP_CTA")}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CustomNavbar;
